import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, Check, Shield, Clock, PoundSterling, Users, Wrench, Award,
  Phone, Star, Heart, Target
} from 'lucide-react';

const values = [
  { icon: Heart, title: 'Traditional Workmanship', description: 'Skills passed down and perfected over generations' },
  { icon: Users, title: 'Clear Communication', description: 'No jargon, no surprises — just straight talk' },
  { icon: Clock, title: 'Reliability', description: 'We show up when promised, every time' },
  { icon: PoundSterling, title: 'Fair Pricing', description: 'Honest quotes with no hidden extras' },
  { icon: Award, title: '12-Month Guarantee', description: 'Industry-leading workmanship warranty' },
  { icon: Target, title: 'Done Properly', description: 'A job worth doing is worth doing right' }
];

const guarantees = [
  'Save 30–60% compared to traditional trades companies',
  '10% Price Guarantee – we\'ll beat any like-for-like quote',
  'Fully vetted, experienced professionals',
  '£15,000,000 public liability insurance',
  'No VAT charged – straightforward pricing',
  'Industry-leading workmanship guarantee'
];

export const About = () => {
  const navigate = useNavigate();

  return (
    <div className="animate-in">
      <SEO 
        title="About Us - Local Handyman Manchester & Cheshire"
        description="Expert-Handyman — your trusted local handyman in Manchester & Cheshire. Door fitting, painting, decorating, skirting boards, plumbing & more. £15M insurance, 12-month guarantee, no VAT. Save 30-60%."
        keywords="about expert handyman, local handyman Manchester, trusted handyman near me, reliable tradesman Cheshire, insured handyman Manchester, handyman company near me"
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-white/90 text-sm font-medium mb-6">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Outfit'] tracking-tight">
              Built on Traditional Values
            </h1>
            <p className="text-lg md:text-xl text-white/80">
              Expert-Handyman was built on traditional workmanship, reliability and a straightforward 
              approach that homeowners, landlords and agents across Greater Manchester rely on.
            </p>
          </div>
        </div>
      </section>

      {/* Personal Story Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-6">
                My Story
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  My background is simple — <strong className="text-foreground">I grew up around tools</strong>. 
                  My dad ran his own building company, and I learned the trade the proper way from a young age. 
                  There's no substitute for hands-on experience passed down through generations.
                </p>
                <p>
                  Later, I passed my CeMAP exams and spent <strong className="text-foreground">6 years working 
                  in and with estate agencies</strong>, gaining a strong understanding of how landlords, tenants 
                  and agents work — and what they expect from tradespeople.
                </p>
                <p>
                  About 12 months ago, I saw a gap in the market for a truly professional, multi-trade property 
                  maintenance service. Not a bloke with a brush and screwdriver having a go — but a 
                  <strong className="text-foreground"> dependable company that delivers high-quality work</strong>, 
                  shows up when promised, communicates clearly and stands by its workmanship.
                </p>
                <p>
                  That's why I started <strong className="text-foreground">Expert-Handyman</strong>.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden bg-muted">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/ce5f41fb9f31d377177e6e5f8441356a8375e2d4465ffa1632c340364de2b838.png"
                  alt="Growing up around tools - where my journey began"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-white p-6 rounded-2xl shadow-xl">
                <p className="text-3xl font-bold font-['Outfit']">6+</p>
                <p className="text-sm text-white/80">Years in property</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two-Tier Model Section */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              One Trusted Contact. Two Tiers of Service.
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Whatever the task, it all starts with Expert-Handyman. One contact, everything covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Expert-Handyman Card */}
            <Card className="border-2 border-primary overflow-hidden">
              <div className="bg-primary text-white p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Wrench className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-['Outfit']">Expert-Handyman</h3>
                    <p className="text-white/80 text-sm">Everyday tasks & repairs</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-4">
                  Most everyday tasks can be handled quickly and cost-effectively — 
                  <strong className="text-foreground"> saving you 30–60%</strong> compared to traditional trades, 
                  with guaranteed workmanship and unbeatable value.
                </p>
                <ul className="space-y-2">
                  {['Decorating & painting', 'Carpentry & joinery', 'General repairs', 'Outdoor & garden work', 'Plumbing basics', 'Light electrical'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Expert-TradesDirect Card */}
            <Card className="border-2 border-amber-500 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-['Outfit']">Expert-TradesDirect</h3>
                    <p className="text-white/80 text-sm">Specialist trade network</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-4">
                  When your project needs a true trade specialist, we connect you with our 
                  <strong className="text-foreground"> hand-picked network of proven professionals</strong> — 
                  without the risk or hassle of searching yourself.
                </p>
                <ul className="space-y-2">
                  {['Electrical installations', 'Gas & boiler work', 'Roofing & chimney repairs', 'Structural alterations', 'Major plumbing', 'Specialist flooring'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-amber-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-muted-foreground mt-8 max-w-2xl mx-auto">
            <strong className="text-foreground">Not sure which you need?</strong> Don't worry — just describe your job and 
            we'll guide you to the right solution. You'll always get the right person for the job at a fair price.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              What We Stand For
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              If you value traditional standards, straightforward service and work done properly, you're in the right place.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, i) => (
              <Card key={i} className="border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <value.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-2">{value.title}</h3>
                  <p className="text-muted-foreground text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-green-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-6">
                Why Choose Expert-Handyman?
              </h2>
              <p className="text-white/80 text-lg mb-8">
                No middleman. No hidden fees. Just expert British tradespeople saving you money on every project.
              </p>
              <div className="space-y-4">
                {guarantees.map((guarantee, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-white/90">{guarantee}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center lg:text-right">
              <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <p className="text-6xl md:text-7xl font-bold font-['Outfit'] mb-2">30-60%</p>
                <p className="text-xl text-white/80">Average savings</p>
                <p className="text-sm text-white/60 mt-2">compared to traditional trades</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
            Serving Greater Manchester & Cheshire
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            We cover Stockport, Manchester, Cheshire and surrounding areas with a full range of 
            multi-trade services — all under one roof.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Stockport', 'Manchester', 'Cheadle', 'Hazel Grove', 'Bramhall', 'Wilmslow', 'Altrincham', 'Sale', 'Didsbury', 'Chorlton'].map((area, i) => (
              <span key={i} className="px-4 py-2 bg-muted rounded-full text-sm font-medium">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Outfit'] tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Get your free quote today. No obligation, no hidden costs — just honest pricing.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="btn-accent text-lg px-8 py-6 h-auto"
              onClick={() => navigate('/quote')}
            >
              Get Free Quote <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="tel:07300405045"
              className="inline-flex items-center justify-center text-lg px-8 py-6 h-auto bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors"
            >
              <Phone className="mr-2 w-5 h-5" /> 07300 40 50 45
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
